import React from 'react';

const CollectionPHIAuditModalView = ({
  collection,
  phiAuditResult,
  runCollectionPHIAuditPending,
  runPHIAudit,
  isReadOnly,
}) => {
  if (!phiAuditResult) {
    return (
      <div className="text-center py-5 px-3">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
          style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#f0f4ff',
          }}
        >
          <i
            className="wb webconnect-shield-check"
            style={{
              fontSize: '28px',
              color: '#4a6cf7',
            }}
          />
        </div>

        <h5 className="fw-semibold mb-2">PHI Audit Not Available</h5>

        <p className="text-muted mb-4 mx-auto" style={{ maxWidth: '500px' }}>
          This collection hasn&apos;t been audited for potential PHI exposure yet. Run an audit to
          check its fields and public filters for sensitive healthcare information.
        </p>

        <div
          className="border rounded p-3 mb-4 mx-auto text-left"
          style={{
            maxWidth: '520px',
            backgroundColor: '#fafbfc',
          }}
        >
          <div className="d-flex align-items-start mb-3">
            <i className="wb webconnect-search mr-3 mt-1 text-primary" />

            <div>
              <div className="fw-semibold">What will be checked?</div>

              <div className="text-muted small mt-1">
                Potential PHI in collection fields and exposure through public filters.
              </div>
            </div>
          </div>

          <div className="d-flex align-items-start">
            <i className="wb webconnect-shield-check mr-3 mt-1 text-success" />

            <div>
              <div className="fw-semibold">Metadata-only analysis</div>

              <div className="text-muted small mt-1">
                The audit analyzes your collection metadata and does not inspect actual stored
                records.
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          className="btn btn-primary px-4"
          onClick={isReadOnly ? undefined : runPHIAudit}
          disabled={runCollectionPHIAuditPending || isReadOnly}
          title={isReadOnly && 'Read-Only Mode'}
        >
          {runCollectionPHIAuditPending ? (
            <>
              <span
                className="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              />
              Running PHI Audit...
            </>
          ) : (
            <>
              <i className="wb webconnect-shield-check mr-2" />
              Run PHI Audit
            </>
          )}
        </button>
      </div>
    );
  }

  const summary =
    phiAuditResult.summary && typeof phiAuditResult.summary === 'object'
      ? phiAuditResult.summary
      : {};

  const flaggedFields = Array.isArray(phiAuditResult.flaggedFields)
    ? phiAuditResult.flaggedFields
    : [];

  const flaggedFilters = Array.isArray(phiAuditResult.flaggedFilters)
    ? phiAuditResult.flaggedFilters
    : [];

  const totalFindings = flaggedFields.length + flaggedFilters.length;
  const hasFindings = totalFindings > 0;

  const getRiskBadgeClass = (risk) => {
    switch (String(risk || '').toLowerCase()) {
      case 'critical':
        return 'badge';

      case 'high':
        return 'bg-danger';

      case 'medium':
        return 'bg-warning text-dark';

      case 'low':
        return 'bg-success';

      default:
        return 'bg-secondary';
    }
  };

  const getRiskBadgeStyle = (risk) => {
    if (String(risk || '').toLowerCase() === 'critical') {
      return {
        backgroundColor: '#8B0000',
        color: '#fff',
      };
    }

    return {};
  };

  const getRiskLabel = (risk) => String(risk || 'medium').toUpperCase();

  const auditedAt = phiAuditResult.auditedAt ? new Date(phiAuditResult.auditedAt) : null;

  const formattedAuditedAt =
    auditedAt && !Number.isNaN(auditedAt.getTime()) ? auditedAt.toLocaleString() : null;

  return (
    <div>
      <div
        className="d-flex align-items-center justify-content-between p-3 mb-4 rounded"
        style={{
          backgroundColor: hasFindings ? '#fff8f8' : '#f4fbf6',
          border: `1px solid ${hasFindings ? '#f1caca' : '#cce8d3'}`,
        }}
      >
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle mr-3"
            style={{
              width: '42px',
              height: '42px',
              backgroundColor: hasFindings ? '#fde8e8' : '#e3f6e9',
            }}
          >
            <i
              className={`wb ${hasFindings ? 'webconnect-exclamation' : 'webconnect-check'}`}
              style={{
                fontSize: '20px',
                color: hasFindings ? '#dc3545' : '#28a745',
              }}
            />
          </div>

          <div>
            <div className="fw-semibold">
              {collection.collectionTitle || collection.collectionName}
            </div>

            <div className="text-muted small">Collection PHI Security Audit</div>

            {formattedAuditedAt && (
              <div className="text-muted small mt-1">Audited {formattedAuditedAt}</div>
            )}
          </div>
        </div>

        <span className={`badge ${hasFindings ? 'bg-danger' : 'bg-success'}`}>
          {hasFindings ? 'ACTION REQUIRED' : 'PASSED'}
        </span>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <div className="text-muted small mb-1">PHI Fields Detected</div>
            <div
              className={`fs-4 fw-bold ${
                flaggedFields.length > 0 ? 'text-danger' : 'text-success'
              }`}
            >
              {summary.phiFieldsDetected != null ? summary.phiFieldsDetected : flaggedFields.length}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <div className="text-muted small mb-1">Public Filters With PHI</div>
            <div
              className={`fs-4 fw-bold ${
                flaggedFilters.length > 0 ? 'text-danger' : 'text-success'
              }`}
            >
              {summary.filtersWithPhiExposure != null
                ? summary.filtersWithPhiExposure
                : flaggedFilters.length}
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="border rounded p-3 h-100">
            <div className="text-muted small mb-1">Total Findings</div>
            <div className={`fs-4 fw-bold ${totalFindings > 0 ? 'text-danger' : 'text-success'}`}>
              {totalFindings}
            </div>
          </div>
        </div>
      </div>

      {!hasFindings && (
        <div className="text-center border rounded p-4">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
            style={{
              width: '52px',
              height: '52px',
              backgroundColor: '#e8f7ed',
            }}
          >
            <i
              className="fa fa-check"
              style={{
                fontSize: '22px',
                color: '#28a745',
              }}
            />
          </div>

          <h6 className="fw-semibold mb-2">No potential PHI exposure found</h6>

          <p className="text-muted small mb-0">
            No likely PHI exposure was identified in this collection&apos;s fields or public
            filters.
          </p>
        </div>
      )}

      {hasFindings && (
        <>
          {flaggedFields.length > 0 && (
            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-semibold mb-1">PHI Fields</h6>
                  <div className="text-muted small">Fields that may contain unencrypted PHI.</div>
                </div>

                <span className="badge bg-danger">{flaggedFields.length}</span>
              </div>

              <div className="border rounded overflow-hidden">
                {flaggedFields.map((field, index) => {
                  if (!field || typeof field !== 'object') return null;

                  const fieldName = field.fieldName || 'Unknown field';
                  const risk = field.risk || 'medium';
                  const reason =
                    field.reason || 'Potential PHI detected based on the supplied metadata.';

                  return (
                    <div key={`${fieldName}-${index}`} className="p-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="pe-3">
                          <div className="fw-medium">{fieldName}</div>
                          <div className="text-muted small mt-1">{reason}</div>
                        </div>

                        <span
                          className={getRiskBadgeClass(risk)}
                          style={{
                            ...getRiskBadgeStyle(risk),
                            padding: '5px 10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            borderRadius: '999px',
                            display: 'inline-block',
                            lineHeight: 1,
                            color: risk === 'medium' ? '#212529' : '#fff',
                          }}
                        >
                          {getRiskLabel(risk)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {flaggedFilters.length > 0 && (
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div>
                  <h6 className="fw-semibold mb-1">Public Filters Exposing PHI</h6>
                  <div className="text-muted small">
                    Public filters that may expose sensitive information.
                  </div>
                </div>

                <span className="badge bg-danger">{flaggedFilters.length}</span>
              </div>

              <div className="border rounded overflow-hidden">
                {flaggedFilters.map((filter, index) => {
                  if (!filter || typeof filter !== 'object') return null;

                  const filterName = filter.filterName || 'Unnamed Filter';
                  const risk = filter.risk || 'medium';
                  const reason =
                    filter.reason ||
                    'Potential PHI exposure detected based on the supplied metadata.';

                  const exposedPhiFields = Array.isArray(filter.exposedPhiFields)
                    ? filter.exposedPhiFields
                    : [];

                  return (
                    <div key={`${filterName}-${index}`} className="p-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="pe-3">
                          <div className="fw-medium">{filterName}</div>

                          <div className="text-muted small mt-1">{reason}</div>

                          {exposedPhiFields.length > 0 && (
                            <div className="mt-2">
                              {exposedPhiFields.map((field, fieldIndex) => (
                                <span
                                  key={`${String(field)}-${fieldIndex}`}
                                  className="badge bg-light text-dark border mr-1 mb-1"
                                >
                                  {field}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <span
                          className={getRiskBadgeClass(risk)}
                          style={{
                            ...getRiskBadgeStyle(risk),
                            padding: '5px 10px',
                            fontSize: '10px',
                            fontWeight: 600,
                            borderRadius: '999px',
                            display: 'inline-block',
                            lineHeight: 1,
                            color: risk === 'medium' ? '#212529' : '#fff',
                          }}
                        >
                          {getRiskLabel(risk)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CollectionPHIAuditModalView;
